import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api from '../services/api';
import { normalizeDisplayPricePercentage } from '../utils/displayPrice';
import {
  loadInitialAuth,
  saveLocalAuth,
  startLoginAsSession,
  clearLoginAsSession,
  clearLocalAuth,
  isLoginAsSession,
} from '../utils/authStorage';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [displayPricePercentage, setDisplayPricePercentage] = useState(100);
  const [minimumAcceptableCondition, setMinimumAcceptableCondition] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchGeneralSettings = useCallback(async (targetCompanyId) => {
    if (!targetCompanyId) return;

    try {
      const res = await api.get(`/api/general-settings/${targetCompanyId}`);
      const percentage = normalizeDisplayPricePercentage(
        res.data?.display_price_percentage ?? 100,
      );
      setDisplayPricePercentage(percentage);

      const minCondition = res.data?.minimum_acceptable_condition;
      setMinimumAcceptableCondition(
        minCondition != null && minCondition !== ''
          ? Number(minCondition)
          : null,
      );
    } catch (error) {
      console.error('Failed to fetch general settings:', error);
      setDisplayPricePercentage(100);
      setMinimumAcceptableCondition(null);
    }
  }, []);

  useEffect(() => {
    const initialAuth = loadInitialAuth();
    setToken(initialAuth.token);
    setUser(initialAuth.user);
    setCompanyId(initialAuth.companyId);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (token && companyId) {
      fetchGeneralSettings(companyId);
    }
  }, [token, companyId, fetchGeneralSettings]);

  // Always a string, like the value restored from storage after a reload —
  // the server keys extension connections by the string id.
  const toCompanyId = (id) => (id == null ? null : String(id));

  const login = (authToken, userData) => {
    setToken(authToken);
    setUser(userData);
    setCompanyId(toCompanyId(userData.company_id));
    saveLocalAuth(authToken, userData);
    fetchGeneralSettings(userData.company_id);
  };

  const bootstrapLoginAs = (authToken, userData) => {
    startLoginAsSession(authToken, userData);
    setToken(authToken);
    setUser(userData);
    setCompanyId(toCompanyId(userData.company_id));
    fetchGeneralSettings(userData.company_id);
  };

  const exitLoginAsSession = () => {
    clearLoginAsSession();
    setToken(null);
    setUser(null);
    setCompanyId(null);
    setDisplayPricePercentage(100);
    setMinimumAcceptableCondition(null);

    if (window.opener) {
      window.close();
      return true;
    }

    window.location.href = '/login';
    return true;
  };

  const isImpersonating = () => isLoginAsSession();

  const logout = () => {
    setToken(null);
    setUser(null);
    setCompanyId(null);
    setDisplayPricePercentage(100);
    setMinimumAcceptableCondition(null);

    if (isLoginAsSession()) {
      clearLoginAsSession();
      if (window.opener) {
        window.close();
        return;
      }
    } else {
      clearLocalAuth();
    }

    localStorage.removeItem('appState_addProduct');
    localStorage.removeItem('appState_viewProducts');
    localStorage.removeItem('appState_ebaySettings');
    localStorage.removeItem('appState_generalSettings');
    localStorage.removeItem('appState_inviteUsers');
    localStorage.removeItem('sorter_selectedImages');
    localStorage.removeItem('sorter_results');

    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith('appState_') || key.startsWith('sorter_')) {
        localStorage.removeItem(key);
      }
    });
  };

  const isAuthenticated = () => {
    return !!token && !token.startsWith('dummy-') && !token.startsWith('google-');
  };

  const value = {
    token,
    user,
    companyId,
    displayPricePercentage,
    minimumAcceptableCondition,
    loading,
    login,
    bootstrapLoginAs,
    exitLoginAsSession,
    isImpersonating,
    logout,
    isAuthenticated,
    fetchGeneralSettings,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
